import {
  isSpanContextValid,
  SpanKind,
  SpanStatusCode,
  trace,
  TraceFlags,
  type Attributes,
  type Span,
  type Tracer,
} from '@opentelemetry/api';
import { scrubAttributes } from './scrub';
import { Attr } from './attributes';
import { formatTraceparent } from './trace/traceparent';

const TRACER_NAME = 'firmable';

/**
 * Resolve a tracer at CALL time. Never hoist one to module scope.
 *
 * Next compiles each server entry — `instrumentation.js` and every route — as
 * its own bundle, so unless a package is listed in `serverExternalPackages`
 * each bundle gets a private copy of it. `@opentelemetry/api` tolerates that
 * for reads, because the registered provider lives on the shared
 * `globalThis[Symbol.for('opentelemetry.js.api.1')]`. It does NOT tolerate it
 * for `getTracer()` evaluated before `register()` runs: that call misses the
 * global, falls back to the copy's own ProxyTracerProvider, and returns a
 * ProxyTracer whose delegate is only ever set on the copy that actually called
 * `setGlobalTracerProvider`. The tracer then no-ops silently, forever — spans
 * are created, parented, ended, and dropped without a single error.
 *
 * That is not a hypothetical: `search_api` and `inngest.run` spans were both
 * lost this way, and the build output confirms it — the route chunk and
 * `instrumentation.js` each carried their own `registerGlobal`.
 * `serverExternalPackages` reduces the copies but does not eliminate them, so
 * this call-time lookup is the actual guarantee.
 */
export const tracer = (name: string = TRACER_NAME): Tracer => trace.getTracer(name);

/**
 * Wrap an operation in a child span with scrubbed attributes.
 *
 * Shaped after `apps/agents/src/mastra/tools/common/withSpan.ts` so the
 * ergonomics carry over, but runtime-agnostic and safe to call before any
 * provider is registered (the OTel API no-ops rather than throwing).
 */
export async function withSpan<T>(
  name: string,
  attributes: Record<string, unknown>,
  fn: (span: Span) => Promise<T>,
  options: { kind?: SpanKind; tracer?: string } = {},
): Promise<T> {
  const scrubbed = scrubAttributes(attributes);

  return tracer(options.tracer).startActiveSpan(
    name,
    { kind: options.kind ?? SpanKind.INTERNAL, attributes: scrubbed.value as Attributes },
    async (span) => {
      try {
        return await fn(span);
      } catch (error) {
        // Record the error TYPE, never the message — axios errors embed the
        // full request URL and Supabase errors echo filter values.
        span.setAttribute('error.type', (error as Error)?.name ?? 'Error');
        span.setStatus({ code: SpanStatusCode.ERROR });
        throw error;
      } finally {
        span.end();
      }
    },
  );
}

/** Set attributes on whichever span is currently active, if any. */
export function annotateActiveSpan(attributes: Record<string, unknown>): void {
  const span = trace.getActiveSpan();
  if (!span) return;
  span.setAttributes(scrubAttributes(attributes).value as Attributes);
}

/**
 * The active span as a W3C traceparent, for code that roots its own span tree.
 *
 * Mastra mints a fresh trace id unless handed one, so an MCP request with no inbound
 * `traceparent` split its tool spans off from the route span open around them — a
 * parentless trace with no trace of who made the call.
 */
export function activeTraceparent(): string | undefined {
  const spanContext = trace.getActiveSpan()?.spanContext();
  if (!spanContext || !isSpanContextValid(spanContext)) return undefined;
  return formatTraceparent({
    traceId: spanContext.traceId,
    spanId: spanContext.spanId,
    sampled: (spanContext.traceFlags & TraceFlags.SAMPLED) === TraceFlags.SAMPLED,
  });
}

/**
 * Mark the active span as failed, carrying the status the handler is about to return.
 *
 * Setting the attribute alone is not enough. Logfire derives a record's level from
 * the OTel span status, so an attribute without `SpanStatusCode.ERROR` still reads
 * as info — which is precisely the failure mode this whole change exists to fix.
 *
 * Inside a route handler the active span is the `executing api route (app) /…`
 * child, not the root. `ResponseStatusSpanProcessor` lifts it to the root, where
 * RED metrics and alerts can see it.
 */
export function markActiveSpanFailed(status: number, errorType?: string): void {
  const span = trace.getActiveSpan();
  if (!span) return;

  span.setAttributes({
    [Attr.HANDLED_ERROR_STATUS]: status,
    'http.response.status_code': status,
    // Matches what Next's own base-server path sets for a 5xx, so a query written
    // against either looks the same.
    'error.type': errorType || String(status),
  });
  span.setStatus({ code: SpanStatusCode.ERROR });
}

/** Mark a span so the sampler always keeps it, regardless of the trace ratio. */
export const forceSampled = { [Attr.FORCE_SAMPLE]: true } as const;
